import { Link } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";
import Head from "next/head";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import constants from "@/global/config/constants";
import { MLBAdobeScriptHeader } from "@/global/common/mlb-common/MLBAdobeScriptHeader";
import React from "react";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { routes } from "@/global/routes";

export const HoustonAstrosLink = () => (
  <Link
    target="_blank"
    sx={{
      fontWeight: 500,
      color: "primary.main",
      textDecoration: "none",
    }}
    href="https://www.mlb.com/astros/"
  >
    Ballpark App
  </Link>
);

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose your games.",
    description: "Select the games you wish to attend.",
  },

  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description:
      "Tickets will be available in your MLB Ballpark app. Reach out to Live Fan Support for any questions.",
  },
];

export const faqs = [
  {
    title: "When will I receive my tickets?",
    description:
      "If your reservation is accepted, a Houston Astros representative will be in contact with further information, including ticket delivery details.",
  },
  {
    title: "What happens after I purchase a Houston Astros Suite online?",
    description:
      "Following your purchase, a Houston Astros representative will follow up with you to confirm your order and assist with any questions you have in anticipation of your event. Tickets and parking will be added to your Houston Astros account where you’ll be able to access them via the MLB Ballpark app. All suite tickets are digital.",
  },
  {
    title: "What is included in my suite?",
    description:
      "If your reservation is accepted, a Houston Astros representative will be in contact with further information, including details regarding parking and food and beverage options.",
  },
];

export function HoustonAstrosCheckoutTerms() {
  const router = useRouterExtended();

  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from astros.com. You may unsubscribe at any time. By
      checking this box you agree to receive additional commercial emails from
      astros.com and MLB.com. The terms and conditions of your ticket and
      policies applicable to its use are updated to address circumstances
      related to COVID-19 and health and safety. By clicking the Purchase
      Tickets button, you agree to accept all{" "}
      <Link
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        onClick={(ev) => {
          ev?.preventDefault();
          router.pushSchema(routes.houston_astros_terms_and_conditions);
        }}
      >
        Houston Astros Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Houston Astros account. Message and data rates may
      apply Tickets will be made available in the MLB Ballpark mobile
      application only.
    </>
  );
}

export function HoustonAstrosAdditionalDetailsDisclaimerTC() {
  return (
    <>
      By entering my name, checking the box above and clicking Submit Request, I
      hereby (i) agree to the{" "}
      <Link
        target="_blank"
        href="https://flex2.seasonshare.com/houstonastros_suites/houston_astros_terms_and_conditions"
      >
        Houston Astros Terms & Conditions
      </Link>{" "}
      (ii) agree that my reservation will not be complete unless and until I
      receive a notice from the Houston Astros confirming whether or not my
      reservation has been accepted in accordance with our agreement.
    </>
  );
}

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <MLBAdobeScriptHeader />
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1546900222245013');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase", 
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=1546900222245013&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};
